import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import useEmblaCarousel from 'embla-carousel-react';

const heroSlides = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&q=80',
    title: 'Custom Manufactured Couches',
    subtitle: 'Handcrafted excellence for your home',
    cta: 'Get Quote',
    ctaLink: '/quote'
  },
  {
    id: 2,
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/c93i9oc7_WhatsApp%20Image%202026-02-08%20at%2016.14.58.jpeg',
    title: 'Luxury Couch Collections',
    subtitle: 'Premium designs for modern living',
    cta: 'View Collection',
    ctaLink: '/gallery'
  },
  {
    id: 3,
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/44vd5t34_lst7.jpeg',
    title: 'Expert Reupholstery',
    subtitle: 'Transform your old furniture',
    cta: 'Learn More',
    ctaLink: '/services#reupholstery'
  },
  {
    id: 4,
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/wfnsvrjo_lst9.jpeg',
    title: 'Modern Living Room Sets',
    subtitle: 'Complete your space with style',
    cta: 'Shop Now',
    ctaLink: '/gallery'
  }
];

export const HeroSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    // Auto-play
    const autoplay = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 6000);

    return () => {
      emblaApi.off('select', onSelect);
      clearInterval(autoplay);
    };
  }, [emblaApi, onSelect]);

  return (
    <section data-testid="hero-slider" className="relative h-[90vh] min-h-[600px] overflow-hidden">
      <div className="embla h-full" ref={emblaRef}>
        <div className="embla__container h-full flex">
          {heroSlides.map((slide, index) => (
            <div 
              key={slide.id} 
              className="embla__slide flex-[0_0_100%] min-w-0 relative h-full"
            >
              {/* Background Image with Ken Burns Effect */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className={`w-full h-full object-cover ${currentSlide === index ? 'animate-ken-burns' : ''}`}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="relative h-full container-custom flex items-center">
                <AnimatePresence mode="wait">
                  {currentSlide === index && (
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -40 }}
                      transition={{ duration: 0.6 }}
                      className="max-w-2xl"
                    >
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-gold font-body text-sm md:text-base uppercase tracking-widest mb-4"
                      >
                        Last Upholstery
                      </motion.p>
                      <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4 leading-tight"
                      >
                        {slide.title}
                      </motion.h1>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-white/80 font-body text-base md:text-lg mb-8"
                      >
                        {slide.subtitle}
                      </motion.p>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-wrap gap-4"
                      >
                        <Link to={slide.ctaLink}>
                          <Button 
                            data-testid={`hero-cta-${index}`}
                            className="btn-gold rounded-full px-8 py-3 text-base font-semibold"
                          >
                            {slide.cta}
                          </Button>
                        </Link>
                        <Link to="/gallery">
                          <Button 
                            variant="outline"
                            data-testid={`hero-secondary-${index}`}
                            className="rounded-full px-8 py-3 text-base font-semibold border-white text-white hover:bg-white hover:text-leather"
                          >
                            View Collection
                          </Button>
                        </Link>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            data-testid={`hero-dot-${index}`}
            className={`w-3 h-3 rounded-full transition-all ${
              currentSlide === index 
                ? 'bg-gold w-8' 
                : 'bg-white/50 hover:bg-white'
            }`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => emblaApi?.scrollPrev()}
        data-testid="hero-prev-btn"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-gold transition-all"
      >
        <i className="fas fa-chevron-left"></i>
      </button>
      <button
        onClick={() => emblaApi?.scrollNext()}
        data-testid="hero-next-btn"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-gold transition-all"
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    </section>
  );
};

export default HeroSlider;
